import React, { useMemo } from 'react';
import styles from './LearningTrend.module.css';
import type { CorrectionRecord, PracticeRecord } from './TrainingDashboard';

export interface LearningTrendProps {
  corrections: CorrectionRecord[];
  practices: PracticeRecord[];
  /** Number of time buckets to display (default 8) */
  buckets?: number;
}

interface TimeBucket {
  label: string;
  corrections: number;
  accuracy: number;
}

interface CategoryAccuracy {
  category: string;
  accuracy: number;
  trend: 'improving' | 'stable' | 'declining';
  color: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  pricing: 'var(--amber)',
  scope: 'var(--blue)',
  product: 'var(--green)',
  installation: 'var(--purple)',
  accounting: 'var(--red)',
};

function getWeekLabel(date: Date): string {
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = date.getDate();
  return `${month} ${day}`;
}

export const LearningTrend: React.FC<LearningTrendProps> = ({
  corrections,
  practices,
  buckets = 8,
}) => {
  const timeBuckets = useMemo<TimeBucket[]>(() => {
    if (corrections.length === 0) return [];

    const now = new Date();
    const msPerBucket = (7 * 24 * 60 * 60 * 1000); // weekly buckets
    const result: TimeBucket[] = [];

    for (let i = buckets - 1; i >= 0; i--) {
      const bucketEnd = new Date(now.getTime() - i * msPerBucket);
      const bucketStart = new Date(bucketEnd.getTime() - msPerBucket);

      const bucketCorrections = corrections.filter((c) => {
        const t = new Date(c.timestamp).getTime();
        return t > bucketStart.getTime() && t <= bucketEnd.getTime();
      });

      const total = bucketCorrections.length;
      // Accuracy estimate: fewer corrections = higher accuracy
      // 0 corrections = 100% (no errors found), scales down as corrections increase
      const accuracy = Math.max(0, Math.min(100, 100 - total * 5));

      result.push({
        label: getWeekLabel(bucketEnd),
        corrections: total,
        accuracy,
      });
    }

    return result;
  }, [corrections, buckets]);

  const maxCorrections = useMemo(() => {
    return Math.max(1, ...timeBuckets.map((b) => b.corrections));
  }, [timeBuckets]);

  const categoryAccuracies = useMemo<CategoryAccuracy[]>(() => {
    const categories = ['pricing', 'scope', 'product', 'installation', 'accounting'];
    return categories.map((cat) => {
      const catPractices = practices.filter((p) => p.category === cat && p.active);
      const catCorrections = corrections.filter((c) => c.correctionType === cat);

      let accuracy = 0;
      if (catPractices.length > 0) {
        const totalApplied = catPractices.reduce((s, p) => s + p.timesApplied, 0);
        const totalConfirmed = catPractices.reduce((s, p) => s + p.timesConfirmed, 0);
        accuracy = totalApplied > 0 ? Math.round((totalConfirmed / totalApplied) * 100) : 0;
      }

      // Determine trend from recent corrections
      const now = Date.now();
      const recentCutoff = now - 14 * 24 * 60 * 60 * 1000;
      const olderCutoff = now - 28 * 24 * 60 * 60 * 1000;
      const recent = catCorrections.filter(
        (c) => new Date(c.timestamp).getTime() > recentCutoff
      ).length;
      const older = catCorrections.filter((c) => {
        const t = new Date(c.timestamp).getTime();
        return t > olderCutoff && t <= recentCutoff;
      }).length;

      let trend: 'improving' | 'stable' | 'declining' = 'stable';
      if (recent < older) trend = 'improving';
      else if (recent > older) trend = 'declining';

      return {
        category: cat,
        accuracy,
        trend,
        color: CATEGORY_COLORS[cat] || 'var(--amber)',
      };
    });
  }, [corrections, practices]);

  const totalCorrections = corrections.length;
  const totalPractices = practices.filter((p) => p.active).length;
  const overallAccuracy = useMemo(() => {
    const withData = categoryAccuracies.filter((c) => c.accuracy > 0);
    if (withData.length === 0) return 0;
    return Math.round(withData.reduce((s, c) => s + c.accuracy, 0) / withData.length);
  }, [categoryAccuracies]);

  return (
    <div className={styles.container}>
      {/* Summary row */}
      <div className={styles.summaryRow}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryValue}>{totalCorrections}</span>
          <span className={styles.summaryLabel}>Corrections</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryValue}>{totalPractices}</span>
          <span className={styles.summaryLabel}>Practices</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryValue}>{overallAccuracy}%</span>
          <span className={styles.summaryLabel}>Accuracy</span>
        </div>
      </div>

      {/* Corrections over time bar chart */}
      <div className={styles.chart}>
        <div className={styles.chartHeader}>
          <span className={styles.chartLabel}>Corrections Over Time</span>
          <div className={styles.chartLegend}>
            <div className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: 'var(--amber)' }} />
              Corrections
            </div>
          </div>
        </div>

        {timeBuckets.length === 0 ? (
          <div className={styles.emptyState}>No correction data to display</div>
        ) : (
          <>
            <div className={styles.barsContainer}>
              {timeBuckets.map((bucket, i) => (
                <div className={styles.barGroup} key={i}>
                  <div
                    className={`${styles.bar} ${styles.barCorrections}`}
                    style={{
                      height: `${(bucket.corrections / maxCorrections) * 100}%`,
                      minHeight: bucket.corrections > 0 ? '4px' : '0px',
                    }}
                  >
                    <span className={styles.barTooltip}>{bucket.corrections}</span>
                  </div>
                  <span className={styles.barLabel}>{bucket.label}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Accuracy by Category */}
      <div>
        <h4 className={styles.sectionTitle}>Accuracy by Category</h4>
        <div className={styles.categoryAccuracy}>
          {categoryAccuracies.map(({ category, accuracy, trend, color }) => (
            <div className={styles.categoryRow} key={category}>
              <span className={styles.categoryName}>{category}</span>
              <div className={styles.categoryBarContainer}>
                <div
                  className={styles.categoryBarFill}
                  style={{
                    width: accuracy > 0 ? `${accuracy}%` : '0%',
                    background: color,
                  }}
                />
              </div>
              <span className={styles.categoryValue}>
                {accuracy > 0 ? `${accuracy}%` : '--'}
              </span>
              <span
                className={`${styles.trendIndicator} ${
                  trend === 'improving'
                    ? styles.trendUp
                    : trend === 'declining'
                      ? styles.trendDown
                      : styles.trendFlat
                }`}
              >
                {trend === 'improving' && '\u2191'}
                {trend === 'declining' && '\u2193'}
                {trend === 'stable' && '\u2192'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
